import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { clientKeys } from '../queries/keys';
import { clientDetailParams, clientPaths } from '../queries/paths';

// TSS vite/03 — server state for the `clients` entity.
// This module only declares keys, fetchers and invalidation rules; caching,
// deduplication and retries are TanStack Query's job.
//
// The surface mirrors api-reference.md exactly — list, detail, create, update.
// DELETE exists there too (soft delete to status 1, and it demands
// `clients:admin`, a stricter permission than writing) but no screen calls it
// yet, so it is not wrapped here. An unused mutation is a loaded gun.

export const CLIENTS_PAGE_SIZE = 25;   // the backend default; hard cap is 100

// GET /api/v1/admin/clients -> { items, pagination: { total, limit, offset } }
// The whole response is returned instead of just `items`: pagination is part
// of the data, and taking it apart here forces every caller to rebuild it.
//
// `q` searches name and contact — case and accent insensitive, escaped, and
// ignored under two characters. `total` is the total of what MATCHES, so the
// pager keeps telling the truth while a search is on (tier 12, T12.1).
//
// Each item carries `projects_summary` with the four figures, the same shape
// and the same key the detail answers (T12.3). The panel used to work that out
// sideways — asking for every active project and grouping by client — and that
// rodeo is gone.
export function useClientsList({ limit = CLIENTS_PAGE_SIZE, offset = 0, q } = {}) {
    const params = { limit, offset };
    if (q) params.q = q;

    return useQuery({
        queryKey: clientKeys.list(params),
        queryFn: ({ signal }) => api
            .get(clientPaths.list(), { params, signal })
            .then(r => r.data),
        // On a page change, keep showing the previous page while the next one
        // loads. Without this the list unmounts and the screen jumps.
        placeholderData: keepPreviousData,
    });
}

export function useClient(id, { include } = {}) {
    return useQuery({
        queryKey: clientKeys.detail(id),
        queryFn: ({ signal }) => api
            .get(clientPaths.detail(id), { params: clientDetailParams({ include }), signal })
            .then(r => r.data),
        enabled: !!id,
    });
}

// POST /api/v1/admin/clients — 201. Requires `clients:write`.
export function useCreateClient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(clientPaths.list(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: clientKeys.all }),
    });
}

// PATCH and not PUT: the contract takes a partial body and every field is
// optional. Sending the whole record on every edit would overwrite fields the
// form never showed.
export function useUpdateClient(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(clientPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: clientKeys.detail(id) });
            qc.invalidateQueries({ queryKey: clientKeys.all });
        },
    });
}

// The picker's fetcher. It lives here because the endpoint does: a form asking
// for options must not know the URL.
//
// It takes the search term and NOT a page: a picker's job is to find one
// record, and past twenty results the answer is to type more, not to paginate.
export function searchClients(q, signal) {
    const params = { limit: 20, offset: 0 };
    if (q) params.q = q;
    return api
        .get(clientPaths.list(), { params, signal })
        .then(r => r.data.items ?? []);
}
